import { useContext, useState, useMemo, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { BoxFull, Button } from "iuvity-ui-components";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../context/index";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { InputText } from "iuvity-ui-components/dist/inputtext";
import { TableHeader } from "../datatable/TableHeader";
import { Actions } from "../datatable/Actions";
import { Column } from "primereact/column";
import { FormSkeleton } from "../skeleton/FormSkeleton";
import { FilterMatchMode } from "primereact/api";
import { CustomDialog, DialogProps } from "../dialog/CustomDialog";
import { formatStartDateAndEndDate, formatStringToDate2 } from "../../helpers/formatDate";
import { EmptyTemplate } from "../datatable/EmptyTemplate";
import noResultsImage from "../../assets/Illustration_NoResults.svg";
import reactiveIcon from "../../assets/icon-restore.svg";
import { Tag } from "iuvity-ui-components/dist/tag";
import { ActionType } from "../../hooks/usePermissions";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from "primereact/multiselect";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

interface FunctionalityAdminProps {
  data: any;
  functionalitiesAction: (action: ActionType, data: any) => Promise<string>;
  reloadFn: () => void;
  isDeleteComponent?: boolean;
}

const formBlankValues = {
  key: "",
  code: "",
  expeditionDate: "",
  expirationDate: "",
  categoryId: "",
  category: "",
  keyLabel: "",
  keyLabelDescription: "",
  parentId: "",
  statusId: "",
  createdAt: "",
};

export const FunctionalityAdminComponent: React.FC<FunctionalityAdminProps> = ({
  isDeleteComponent = false,
  data,
  functionalitiesAction,
  reloadFn,
}) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { language, pageFilter, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: formBlankValues,
  });

  const { control, setValue, getValues } = form;

  const columns = useMemo(() => {
    if (language) {
      let columList = [
        {
          field: "key",
          header: t("Functionalities.keyLabel"),
        },
        {
          field: "code",
          header: t("Functionalities.codeLabel"),
        },
        {
          field: "keyLabelDescription",
          header: t("Functionalities.keyLabelDescriptionLabel"),
        },
        {
          field: "keyLabel",
          header: t("Functionalities.keyLabelLabel"),
        },
        {
          field: "parentId",
          header: t("Functionalities.parentIdLabel"),
        },
        {
          field: "statusId",
          header: t("Functionalities.statusIdLabel"),
        },
        {
          field: "categoryId",
          header: t("Functionalities.categoryIdLabel"),
        },
        {
          field: "category",
          header: t("Functionalities.categoryLabel"),
        },
        {
          field: "expeditionDate",
          header: t("Functionalities.expeditionDateLabel"),
        },
        {
          field: "expirationDate",
          header: t("Functionalities.expirationDateLabel"),
        },
        {
          field: "createdAt",
          header: t("Functionalities.createdAtLabel"),
        },
        {
          field: "deletedAt",
          header: t("Functionalities.deletedAtLabel"),
        },
        {
          field: "actions",
          header: t("Functionalities.actionsLabel"),
        },
      ];
      return isDeleteComponent
        ? columList.filter((obj) => obj.field !== 'statusId' && obj.field !== 'createdAt')
        : columList.filter((obj) => obj.field !== 'deletedAt')
    } else {
      return [];
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, language]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    key: { value: pageFilter?.key, matchMode: FilterMatchMode.IN },
    code: { value: pageFilter?.code, matchMode: FilterMatchMode.IN },
    expeditionDate: { value: pageFilter?.expeditionDate, matchMode: FilterMatchMode.CONTAINS },
    expirationDate: { value: pageFilter?.expirationDate, matchMode: FilterMatchMode.CONTAINS },
    categoryId: { value: pageFilter?.categoryId, matchMode: FilterMatchMode.CONTAINS },
    category: { value: pageFilter?.category, matchMode: FilterMatchMode.CONTAINS },
    keyLabel: { value: pageFilter?.keyLabel, matchMode: FilterMatchMode.IN },
    keyLabelDescription: { value: pageFilter?.keyLabelDescription, matchMode: FilterMatchMode.CONTAINS },
    parentId: { value: pageFilter?.parentId, matchMode: FilterMatchMode.CONTAINS },
    statusId: { value: pageFilter?.statusId, matchMode: FilterMatchMode.CONTAINS },
    createdAt: { value: pageFilter?.createdAt, matchMode: FilterMatchMode.CONTAINS },
  }
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [administrationFilters, setAdministrationFilters] = useState(defaultFilterValues);
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const onAction = (type: "CLONE" | "UPDATE" | "DETAIL", data?: any) => {
    if (type === "UPDATE" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedFunctionality: data,
      }));
      navigate("/functionalities/editfunctionalities");
    }
    if (type === "DETAIL" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedFunctionality: data,
      }));
      navigate("/functionalities/detailfunctionalities");
    }
    if (type === "CLONE" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedFunctionality: data,
      }));
      navigate("/functionalities/clonefunctionalities");
    }
  };

  const onDeleteAction = async (data: any) => {
    const actionResult = await functionalitiesAction("DELETE", {key: data?.key, code: data?.code});
    if (actionResult === "SUCCESS") {
      reloadFn();
      setAdministrationFilters(defaultFilterValues);
    }
  };

  const onReactiveAction = async (data: any) => {
    const actionResult = await functionalitiesAction("REACTIVE", {key: data?.key, code: data?.code});
    if (actionResult === "SUCCESS") {
      reloadFn();
      setAdministrationFilters(defaultFilterValues);
    }
  };

  const handleModalProps = async (type: 'DELETE' | 'REACTIVE', data: any) => {
    console.log(data);
    setModalProps((prev:any) => ({
      ...prev,
      visible: true,
      title: type === 'DELETE' ? t("Functionalities.deleteModalTitle") : t("Functionalities.reactiveModalTitle"),
      content: `${t("Functionalities.modalContent1")} "${data.key}" ${type === 'DELETE' ?  t("Functionalities.deleteModalContent2") : t("Functionalities.reactiveModalContent2")}`,
      onConfirm: () => {
        type === 'DELETE' ? onDeleteAction(data!) : onReactiveAction(data!);
      },
    }));
  };

  const onClearFiltersColumn = () => {
    setAdministrationFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues);
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Functionalities.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`Functionalities.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`Functionalities.${options.field}PlaceholderLabel`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const filterClearTemplate = (options: any) => {
      return (
        <Button
          label={t("Functionalities.clearFilterLabel")}
          variant="p-button-text"
          onClick={onClearFiltersColumn}
        />
      )
  };
    
  const filterApplyTemplate = (options: any) => {
      return (
        <Button
          label={t("Functionalities.applyFilterLabel")}
          className="p-button"
          onClick={()=>{
            options.filterApplyCallback();
            setAppState((prev: any)=>({
              ...prev,
              pageFilter: getValues(),
            }));
          }}
        />
      )
  };

  const statusBodyTemplate = (rowData:any) => (
    <Tag
      value={rowData.statusId}
      severity={
        (['active', 'activo'].includes(rowData.statusId.toLowerCase()) && 'success') ||
        (['draft', 'borrador'].includes(rowData.statusId.toLowerCase()) && 'info') ||
        (['inactive', 'inactivo'].includes(rowData.statusId.toLowerCase()) && 'danger') ||
        'warning'
      }
    />
  );

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option}</span>
      </div>
    );
  };
  const multiselectFilterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          // rules={PermissionsFormSchema(t).name}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
              options.filterCallback(e.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-1">
                  {t(`Functionalities.${options.field}Label`)}
                </label>
                <MultiSelect
                  display="chip"
                  options={[...new Set(data.filter((functionality: any) => functionality.statusId !== 'DELETED').map((functionality: any) => functionality[`${options.field}`]))]} 
                  itemTemplate={ItemsTemplate}
                  placeholder={t(`Functionalities.${options.field}PlaceholderLabel`)} 
                  className="mb-2"
                  emptyFilterMessage={t("Functionalities.noAvailableMessage")}
                  filter
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const statuses = [
    `${t("Functionalities.ACTIVELabel")}`,
    `${t("Functionalities.INACTIVELabel")}`,
    `${t("Functionalities.DRAFTLabel")}`,
  ];
  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  };
  const statusFilterTemplate = (options: any) => {
    return (
      <Controller
            name={options.field}
            control={control}
            render={({ field }) => {
              field.onChange = (e) => {
                setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
                options.filterCallback(e.value);
              };
              return (
                <>
                  <label htmlFor={options.field} className="block lb mt-2 mb-3">
                    {t(`Permissions.${options.field}Label`)}
                  </label>
                  {/* // @ts-ignore */}
                  <Dropdown
                    options={statuses}
                    itemTemplate={statusItemTemplate}
                    placeholder={t(`Permissions.${options.field}PlaceholderLabel`)}
                    className="p-column-filter mb-2"
                    showClear={false}
                    {...field} 
                  />
                </>
              );
            }}
          />
      
    )
  };

  useEffect(() => {
    setAdministrationFilters(defaultFilterValues);
  }, [pageFilter]);

  return (
    <>
      {!isDeleteComponent &&
        <BoxFull className="mt-0 mx-0 px-0 pb-3" variant="tertiary">
          <div className="subheader-buttons">
            <SubheaderActions
                className="p-0 m-0"
                actions={subheaderActionsPrivilegeValidator([
                  {
                    id: "create-catalogue",
                    icon: "iu iu-icon-plus",
                    label: t("Functionalities.newFunctionalitiesBtnLabel"),
                    style: {
                      display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
                    },
                    onClick: () => navigate("/functionalities/newfunctionalities"),
                  },
                ])}
              />
          </div>
        </BoxFull>
      }
      <BoxFull className={`dt-c mx-0 ${isDeleteComponent && "mt-5"} mb-4`} variant="tertiary" style={{ backgroundColor: 'var(--cardBackgroundColorOne)' }}>
        {data ? (
          <DataTable
            header={
              <TableHeader
                title={t(`Functionalities.functionalitiesTableTitle`)}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={administrationFilters}
                setFilters={setAdministrationFilters}
                reload={reloadFn}
                onClearFiltersColumn={onClearFiltersColumn}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={<EmptyTemplate image={noResultsImage} text={t('Functionalities.emptyDataTableMessage')} />}
            rows={10}
            size="normal"
            filters={administrationFilters}
            sortField="updatedAt"
            dataKey="catalogue-id"
            sortOrder={-1}
            value={
              data?.filter((functionalityItem: any) => {
                return isDeleteComponent
                  ? functionalityItem?.statusId === 'DELETED'
                  : functionalityItem?.statusId !== 'DELETED'
              })
              .map((functionalityItem: any) => ({
              ...functionalityItem,
              keyLabel: functionalityItem?.keyLabel?.includes('Menu.') ? functionalityItem?.keyLabel?.split('.')[1] : functionalityItem?.keyLabel,
              categoryId: functionalityItem?.category.name,
              category: functionalityItem?.category?.parent?.name,
              statusId: t(`Functionalities.${functionalityItem?.statusId}Label`),
              expeditionDate: !!functionalityItem?.expeditionDate ? formatStartDateAndEndDate(functionalityItem?.expeditionDate, true, t) : null,
              expirationDate: !!functionalityItem?.expirationDate ? formatStartDateAndEndDate(functionalityItem?.expirationDate, true, t) : null,
              createdAt: !!functionalityItem?.createdAt ? formatStringToDate2(functionalityItem?.createdAt, true, t) : null,
              deletedAt: !!functionalityItem?.deletedAt ? formatStringToDate2(functionalityItem?.deletedAt, true, t) : null,
              actions: !isDeleteComponent
              ? (
                  <Actions
                    enableDelete
                    auxButtons={[
                      {
                        label: t("Roles.detailBtnLabel"),
                        icon: "iu-icon-detail",
                        function: () => {
                          onAction("DETAIL", functionalityItem);
                          window.scrollTo(0,0);
                        },
                        color: "var(--successColor)",
                        order: "-3",
                      },
                      {
                        label: t("Functionalities.cloneBtnLabel"),
                        icon: "iu-icon-copy",
                        function: () => onAction("CLONE", functionalityItem),
                        color: "var(--successColor)",
                        order: "-1",
                        showButton: permissiondInfoByRole.CREATE
                      }
                    ]}
                    onEdit={() => onAction("UPDATE", functionalityItem)}
                    onDelete={() => handleModalProps('DELETE', functionalityItem)}
                    visibleObject={{...permissiondInfoByRole}}
                  />
                )
              : (
                <Button
                  className="p-button-rounded p-button-success p-button-outlined act-btn"
                  aria-label="reactive"
                  tooltip={t("Functionalities.reactiveLabel")}
                  tooltipOptions={{ position: "bottom" }}
                  variant="p-button"
                  onClick={() => handleModalProps('REACTIVE', functionalityItem)}
                  style={{
                    color: "var(--successColor)",
                  }}
                >
                  <img
                    alt='reactive-icon'
                    src={reactiveIcon}
                    width='15px'
                  />
                </Button>
              )
                ,
              }))}
            scrollable
            resizableColumns
            globalFilterFields={[
              "statusId",
              "key",
              "code",
              "port",
              "expeditionDate",
              "expirationDate",
              "categoryId",
              "category",
              "keyLabel",
              "keyLabelDescription",
              "parentId",
              "createdAt",
              "deletedAt",
            ]}
            filterDisplay="menu"
            rowsPerPageOptions={[10,20,50]}
            >
            {selectedColumns.map((col: any) => {
              return (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable={!(col.field === "actions")}
                  align={col.field === "actions"  ? "center" : "left"}
                  headerStyle={
                    col.field === "actions" 
                    ? { justifyContent: "center" }
                    : undefined
                  }
                  style={{
                    boxShadow:
                    col.field === "actions"
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                    maxWidth: col.field === "actions" ? "8.25rem" : "100%",
                    whiteSpace: "normal",
                    minWidth: (col.field === "actions") ? "12rem" : "14rem",
                  }}
                  frozen={col.field === "actions" ? true : false}
                  alignFrozen={col.field === "actions" ? "right" : undefined}
                  filterField={col.field}
                  showFilterMatchModes={false}
                  filterMenuStyle={{ width: "23rem" }}
                  filter={col.field === "actions" ? false : true}
                  filterElement={
                    (col.field === "statusId" && statusFilterTemplate) ||
                    ((col.field === "key" || col.field === "code" || col.field === "keyLabel") && multiselectFilterTemplate) ||
                    (col.field !== "statusId" && filterTemplate)
                  }
                  filterApply={filterApplyTemplate}
                  filterClear={filterClearTemplate}
                  body={col.field === "statusId" ? statusBodyTemplate : undefined}
                />
                );
              })}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
          )}
      </BoxFull>
      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
